import React, { useState, useEffect } from 'react';
import Button from '../../components/common/button/Button';
import Modal from '../../components/common/model/Modal';
import styles from './InvoiceList.module.scss';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import invoiceService from '../../services/invoiceService';
import productService from '../../services/productService';
import customerService from '../../services/customerService';
import InputField from '../../components/common/inputField/InputField';
import { Invoice, CreateInvoiceItemData, CreateInvoiceData } from '../../types/invoiceTypes';
import { Product } from '../../types/productTypes';
import { Customer } from '../../types/customerTypes';

const InvoiceList: React.FC<{ role?: string }> = ({ role = 'admin' }) => {
    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [products, setProducts] = useState<Product[]>([]);
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
    const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
    const [customerSearch, setCustomerSearch] = useState('');
    const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
    const [date, setDate] = useState<string>(new Date().toLocaleDateString('en-CA'));

    const [form, setForm] = useState({
        customer_id: '',
        notes: '',
        cgst_amount: '',
        sgst_amount: '',
        discount_amount: '',
        status: 'pending',
        payment_status: 'unpaid'
    });

    const [items, setItems] = useState<CreateInvoiceItemData[]>([{
        product_id: 0,
        description: '',
        quantity: 1,
        unit_price: 0,
        amount: 0,
        empty_stock_count: 0
    }]);

    const [productSearches, setProductSearches] = useState<string[]>(['']);
    const [showProductDropdowns, setShowProductDropdowns] = useState<boolean[]>([false]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [editMode, setEditMode] = useState(false);
    const [editingInvoiceId, setEditingInvoiceId] = useState<string | null>(null);
    const [originalItems, setOriginalItems] = useState<CreateInvoiceItemData[]>([]);

    const fetchInvoices = async () => {
        try {
            setLoading(true);
            const filters = {
                status: filterStatus || undefined,
                date: date || undefined,
            };
            const response = await invoiceService.getInvoices(filters);
            console.log("response:::::::", response)
            setInvoices(Array.isArray(response) ? response : []);
        } catch (err) {
            console.error('Error fetching invoices:', err);
            toast.error('Failed to fetch invoices');
            setInvoices([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchProducts = async () => {
        try {
            const response = await productService.getProducts();
            setProducts(Array.isArray(response) ? response : []);
        } catch (err) {
            console.error('Error fetching products:', err);
            toast.error('Failed to fetch products');
        }
    };

    const fetchCustomers = async () => {
        try {
            const response = await customerService.getCustomers();
            setCustomers(Array.isArray(response) ? response : []);
        } catch (err) {
            console.error('Error fetching customers:', err);
            toast.error('Failed to fetch customers');
        }
    };

    useEffect(() => {
        fetchInvoices();
        fetchProducts();
        fetchCustomers();
    }, [filterStatus]);

    useEffect(() => {
        fetchInvoices();
        fetchProducts();
        fetchCustomers();
    }, [date]);

    const calculateItemAmount = (item: CreateInvoiceItemData) => {
        const baseAmount = item.quantity * item.unit_price;
        return baseAmount;
    };

    const calculateTotals = () => {
        const subtotal = items.reduce(
            (sum, item) => sum + calculateItemAmount(item),
            0
        );

        const baseAmount = subtotal/1.18
        const taxAmt  = subtotal - baseAmount

        const cgst = taxAmt/2;
        const sgst = taxAmt/2;
        const discount = parseFloat(form.discount_amount) || 0;
        return {
            subtotal: baseAmount,
            cgst,
            sgst,
            discount,
            total: subtotal - discount
        };
    };

    // Get total quantity used for a product across all items (excluding a specific index if provided)
    const getTotalProductQuantity = (productId: number, excludeIndex?: number): number => {
        return items.reduce((total, item, index) => {
            if (item.product_id === productId && index !== excludeIndex) {
                return total + item.quantity;
            }
            return total;
        }, 0);
    };

    // Get stock for a product (without edit mode adjustment)
    const getProductStock = (productId: number): number => {
        const product = products.find(p => Number(p.id) === productId);
        return product?.full_stock_count || 0;
    };

    // Get available stock for a product (accounting for edit mode and other items using same product)
    const getAvailableStock = (productId: number, currentIndex?: number): number => {
        const product = products.find(p => Number(p.id) === productId);
        const currentStock = product?.full_stock_count || 0;
        
        // Subtract quantities already used in OTHER items with the same product
        const usedInOtherItems = getTotalProductQuantity(productId, currentIndex);
        let availableStock = currentStock - usedInOtherItems;
        
        // In edit mode, add back the ORIGINAL quantity for this product from the original invoice
        if (editMode && originalItems.length > 0) {
            const totalOriginalQuantity = originalItems.reduce((total, item) => {
                if (item.product_id === productId) {
                    return total + item.quantity;
                }
                return total;
            }, 0);
            availableStock += totalOriginalQuantity;
        }
        
        return Math.max(0, availableStock);
    };

    // Check if quantity exceeds available stock
    const isLowStock = (productId: number, quantity: number, itemIndex: number): boolean => {
        if (!productId) return false;
        const availableStock = getAvailableStock(productId, itemIndex);
        return quantity > availableStock;
    };

    // Check if product is out of stock
    const isOutOfStock = (productId: number, itemIndex?: number): boolean => {
        if (!productId) return false;
        const availableStock = getAvailableStock(productId, itemIndex);
        return availableStock === 0;
    };

    const handleCustomerSelect = (customer: Customer) => {
        setSelectedCustomer(customer);
        setForm({ ...form, customer_id: customer.id.toString() });
        setCustomerSearch(`${customer.name} - ${customer.phone}`);
        setShowCustomerDropdown(false);
    };

    const handleProductSelect = (index: number, product: Product) => {
        // Check if product is out of stock (considering edit mode and other items)
        if (isOutOfStock(Number(product.id), index)) {
            toast.error(`${product.name} is out of stock!`);
            return;
        }

        const newItems = [...items];
        newItems[index] = {
            ...newItems[index],
            product_id: Number(product.id),
            description: product.name,
            unit_price: product.price,
            quantity: 1, // Reset to 1 when selecting new product
            amount: product.price,
            empty_stock_count: 0 // Reset empty count when selecting new product
        };
        setItems(newItems);

        const newSearches = [...productSearches];
        newSearches[index] = product.name;
        setProductSearches(newSearches);

        const newDropdowns = [...showProductDropdowns];
        newDropdowns[index] = false;
        setShowProductDropdowns(newDropdowns);
    };

    const handleItemChange = (index: number, field: keyof CreateInvoiceItemData, value: any) => {
        const newItems = [...items];
        const currentItem = newItems[index];

        // Validate quantity if it's being changed
        if (field === 'quantity') {
            const newQuantity = parseInt(value) || 0;
            const availableStock = getAvailableStock(currentItem.product_id, index);
            
            if (newQuantity > availableStock) {
                toast.error(`Only ${availableStock} units available in stock for this product!`);
                return;
            }
        }

        // Validate empty_stock_count - cannot exceed quantity
        if (field === 'empty_stock_count') {
            const newEmptyCount = parseInt(value) || 0;
            // if (newEmptyCount > currentItem.quantity) {
            //     toast.error(`Empty stock count cannot exceed quantity (${currentItem.quantity})!`);
            //     return;
            // }
            if (newEmptyCount < 0) {
                toast.error('Empty stock count cannot be negative!');
                return;
            }
        }

        newItems[index] = { ...newItems[index], [field]: value };

        if (field === 'quantity' || field === 'unit_price') {
            newItems[index].amount = calculateItemAmount(newItems[index]);
        }

        // If quantity changes and it's less than empty_stock_count, adjust empty_stock_count
        if (field === 'quantity') {
            const newQuantity = parseInt(value) || 0;
            if (newItems[index].empty_stock_count > newQuantity) {
                newItems[index].empty_stock_count = newQuantity;
            }
        }

        setItems(newItems);
    };

    const addItem = () => {
        setItems([...items, {
            product_id: 0,
            description: '',
            quantity: 1,
            unit_price: 0,
            amount: 0,
            empty_stock_count: 0
        }]);
        setProductSearches([...productSearches, '']);
        setShowProductDropdowns([...showProductDropdowns, false]);
    };

    const removeItem = (index: number) => {
        if (items.length > 1) {
            setItems(items.filter((_, i) => i !== index));
            setProductSearches(productSearches.filter((_, i) => i !== index));
            setShowProductDropdowns(showProductDropdowns.filter((_, i) => i !== index));
        }
    };

    const validateStock = (): boolean => {
        // Create a map to track total quantities per product
        const productQuantities = new Map<number, { total: number; indices: number[] }>();
        
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (!item.product_id || !item.description) {
                toast.error(`Please select a product for item ${i + 1}`);
                return false;
            }

            // Track quantities per product
            if (productQuantities.has(item.product_id)) {
                const existing = productQuantities.get(item.product_id)!;
                existing.total += item.quantity;
                existing.indices.push(i + 1);
            } else {
                productQuantities.set(item.product_id, {
                    total: item.quantity,
                    indices: [i + 1]
                });
            }
        }

        // Validate each product's total quantity
        let isValid = true;
        productQuantities.forEach((data, productId) => {
            if (!isValid) return;
            
            const product = products.find(p => Number(p.id) === productId);
            const currentStock = product?.full_stock_count || 0;
            
            // Calculate available stock considering edit mode
            let availableStock = currentStock;
            if (editMode && originalItems.length > 0) {
                const totalOriginalQuantity = originalItems.reduce((total, item) => {
                    if (item.product_id === productId) {
                        return total + item.quantity;
                    }
                    return total;
                }, 0);
                availableStock += totalOriginalQuantity;
            }

            if (data.total > availableStock) {
                const itemNumbers = data.indices.join(', ');
                toast.error(
                    `Total quantity (${data.total}) for "${product?.name}" across items ${itemNumbers} exceeds available stock (${availableStock} units)`
                );
                isValid = false;
            }
        });
        
        if (!isValid) return false;
        
        return true;
    };

    const handleAdd = async () => {
        if (!form.customer_id) {
            toast.error('Please select a customer');
            return;
        }

        if (items.length === 0 || !items[0].description) {
            toast.error('Please add at least one item');
            return;
        }

        // Validate stock before submission
        if (!validateStock()) {
            return;
        }

        const data = {
            customer_id: parseInt(form.customer_id),
            items: items.map(item => ({
                ...item,
                amount: calculateItemAmount(item),
                empty_stock_count: item.empty_stock_count || 0
            })),
            notes: form.notes,
            cgst_amount: parseFloat(form.cgst_amount) || 0,
            sgst_amount: parseFloat(form.sgst_amount) || 0,
            discount_amount: parseFloat(form.discount_amount) || 0,
            status: form.status as any,
            payment_status: form.payment_status as any
        };

        try {
            await invoiceService.createInvoice(data);
            toast.success('Invoice created successfully!');
            setShowModal(false);
            resetForm();
            fetchInvoices();
            fetchProducts(); // Refresh products to get updated stock
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to create invoice';
            toast.error(message);
        }
    };

    const handleSaveEdit = async () => {
        if (!editingInvoiceId) return;

        // Validate stock before submission
        if (!validateStock()) {
            return;
        }

        const data = {
            id: editingInvoiceId,
            customer_id: parseInt(form.customer_id),
            items: items.map(item => ({
                ...item,
                amount: calculateItemAmount(item),
                empty_stock_count: item.empty_stock_count || 0
            })),
            notes: form.notes,
            cgst_amount: parseFloat(form.cgst_amount) || 0,
            sgst_amount: parseFloat(form.sgst_amount) || 0,
            discount_amount: parseFloat(form.discount_amount) || 0,
            status: form.status as any,
            payment_status: form.payment_status as any
        };

        try {
            await invoiceService.updateInvoice(data);
            toast.success('Invoice updated successfully!');
            setShowModal(false);
            setEditMode(false);
            setEditingInvoiceId(null);
            resetForm();
            fetchInvoices();
            fetchProducts(); // Refresh products to get updated stock
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to update invoice';
            toast.error(message);
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Are you sure you want to delete this invoice?')) {
            return;
        }

        try {
            await invoiceService.deleteInvoice(id);
            toast.success('Invoice deleted successfully!');
            fetchInvoices();
            fetchProducts(); // Refresh products to get updated stock
        } catch (err) {
            toast.error('Failed to delete invoice');
        }
    };

    const handleViewInvoice = async (id: string) => {
        try {
            window.location.href = `/orders/${id}`;
        } catch (err) {
            toast.error('Failed to load invoice details');
        }
    };

    const handleOpenEditModal = async (invoice: Invoice) => {
        try {
            const fullInvoice = await invoiceService.getInvoiceById(invoice.id);
            setForm({
                customer_id: fullInvoice.customer_id.toString(),
                notes: fullInvoice.notes || '',
                cgst_amount: fullInvoice.cgst_amount.toString(),
                sgst_amount: fullInvoice.sgst_amount.toString(),
                discount_amount: fullInvoice.discount_amount.toString(),
                status: fullInvoice.status,
                payment_status: fullInvoice.payment_status
            });

            const customer = customers.find(c => Number(c.id) === fullInvoice.customer_id);
            if (customer) {
                setSelectedCustomer(customer);
                setCustomerSearch(customer.name);
            }

            // Store original items for stock calculation (including empty_stock_count)
            const itemsWithEmptyCount = (fullInvoice.items || []).map(item => ({
                ...item,
                empty_stock_count: item.empty_stock_count || 0
            }));
            setOriginalItems(itemsWithEmptyCount);
            setItems(itemsWithEmptyCount);
            setProductSearches(fullInvoice.items?.map(item => item.description) || ['']);
            setShowProductDropdowns(fullInvoice.items?.map(() => false) || [false]);
            setEditingInvoiceId(fullInvoice.id);
            setEditMode(true);
            setShowModal(true);
        } catch (err) {
            toast.error('Failed to load invoice for editing');
        }
    };

    const resetForm = () => {
        setForm({
            customer_id: '',
            notes: '',
            cgst_amount: '',
            sgst_amount: '',
            discount_amount: '',
            status: 'pending',
            payment_status: 'unpaid'
        });
        setItems([{
            product_id: 0,
            description: '',
            quantity: 1,
            unit_price: 0,
            amount: 0,
            empty_stock_count: 0
        }]);
        setSelectedCustomer(null);
        setCustomerSearch('');
        setProductSearches(['']);
        setShowProductDropdowns([false]);
        setOriginalItems([]);
    };

    const filteredInvoices = (invoices || []).filter(invoice =>
        (invoice.invoice_number?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        (invoice.customer_name?.toLowerCase() || '').includes(searchTerm.toLowerCase())
    );

    const filteredCustomers = customers.filter(customer =>
        customer.name.toLowerCase().includes(customerSearch.toLowerCase()) ||
        customer.phone?.includes(customerSearch)
    );

    const getFilteredProducts = (index: number) => {
        const search = productSearches[index] || '';
        return products.filter(product =>
            product.name.toLowerCase().includes(search.toLowerCase())
        );
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
        }).format(amount);
    };

    const getSubTotal = (amount: number, discount_amount: number, cgst_amount: number, sgst_amount: number) => {
        const subtotal = amount + discount_amount - (cgst_amount + sgst_amount)
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
        }).format(subtotal);
    };

    const getStatusBadge = (status: string) => {
        const badges: any = {
            draft: { color: '#94a3b8', bg: '#f1f5f9' },
            pending: { color: '#f59e0b', bg: '#fef3c7' },
            paid: { color: '#10b981', bg: '#d1fae5' },
            partial: { color: '#3b82f6', bg: '#dbeafe' },
            cancelled: { color: '#ef4444', bg: '#fee2e2' }
        };
        const badge = badges[status] || badges.draft;
        return (
            <span style={{
                padding: '4px 12px',
                borderRadius: '12px',
                fontSize: '0.75rem',
                fontWeight: '600',
                backgroundColor: badge.bg,
                color: badge.color
            }}>
                {status.toUpperCase()}
            </span>
        );
    };

    const totals = calculateTotals();

    useEffect(() => {
        setForm(prev => ({
            ...prev,
            cgst_amount: totals.cgst.toString(),
            sgst_amount: totals.sgst.toString()
        }));
    }, [totals.cgst, totals.sgst]);

    return (
        <div className={styles.container}>
            <h4>Invoices</h4>
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <div className={styles.searchContainer}>
                        <div className={styles.searchWrapper}>
                            <svg className={styles.searchIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="11" cy="11" r="8"></circle>
                                <path d="m21 21-4.35-4.35"></path>
                            </svg>
                            <input
                                type="text"
                                placeholder="Search by invoice number or customer..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className={styles.searchInput1}
                            />
                            {searchTerm && (
                                <button className={styles.clearButton} onClick={() => setSearchTerm('')}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="18" y1="6" x2="6" y2="18"></line>
                                        <line x1="6" y1="6" x2="18" y2="18"></line>
                                    </svg>
                                </button>
                            )}
                        </div>
                    </div>
                    <input
                        type="date"
                        value={date}
                        max={new Date().toLocaleDateString('en-CA')}
                        onChange={(e) => {
                            setDate(e.target.value);
                        }}
                        className={styles.dateFilter}
                        placeholder="Select date..."
                    />
                </div>
                <div className={styles.headerRight}>
                    {/* {role === 'admin' && ( */}
                        <Button variant="primary" onClick={() => setShowModal(true)}>
                            + Create Invoice
                        </Button>
                    {/* )} */}
                </div>
            </div>

            <div className={styles.tableContainer}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>Invoice #</th>
                            <th>Customer</th>
                            <th>Sub Total</th>
                            <th>CGST</th>
                            <th>SGST</th>
                            <th>Total Amount</th>
                            <th>Date</th>
                             <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={role === 'admin' ? 9 : 8} style={{ textAlign: 'center', padding: '20px' }}>
                                    Loading invoices...
                                </td>
                            </tr>
                        ) : filteredInvoices.length > 0 ? (
                            filteredInvoices.map(invoice => (
                                <tr key={invoice.id}>
                                    <td>{invoice.invoice_number}</td>
                                    <td>{invoice.customer_name || '-'}</td>
                                    <td>{getSubTotal(Number(invoice.total_amount), Number(invoice.discount_amount), Number(invoice.cgst_amount), Number(invoice.sgst_amount)) || '-'}</td>
                                    <td>{formatCurrency(invoice.cgst_amount) || '-'}</td>
                                    <td>{formatCurrency(invoice.sgst_amount) || '-'}</td>
                                    <td>{formatCurrency(invoice.total_amount)}</td>
                                    <td>{new Date(invoice.created_at).toLocaleDateString()}</td>
                                    
                                        <td>
                                            <div className={styles.actionIcons}>
                                                <Button onClick={() => handleViewInvoice(invoice.id)} className={styles.actionButton}>
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                                        <circle cx="12" cy="12" r="3"></circle>
                                                    </svg>
                                                </Button>
                                                {role === 'admin' && (
                                                    <>
                                                <Button onClick={() => handleOpenEditModal(invoice)} className={styles.actionButton}>
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M12 20h9" />
                                                        <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19.5 3 21l1.5-4L16.5 3.5z" />
                                                    </svg>
                                                </Button>
                                                <Button onClick={() => handleDelete(invoice.id)} className={styles.actionButton}>
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2">
                                                        <polyline points="3 6 5 6 21 6"></polyline>
                                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"></path>
                                                    </svg>
                                                </Button>
                                                </>
                                                )}
                                            </div>
                                        </td>
                                    
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={role === 'admin' ? 9 : 8} style={{ textAlign: 'center', padding: '20px' }}>
                                    No invoices found
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Create/Edit Invoice Modal */}
            <Modal
                isOpen={showModal}
                onClose={() => {
                    setShowModal(false);
                    setEditMode(false);
                    setEditingInvoiceId(null);
                    resetForm();
                }}
                title={editMode ? "Edit Invoice" : "Create Invoice"}
                size="xl"
            >
                <div className={styles.modalForm}>
                    {/* Customer Search */}
                    <div className={styles.formRow}>
                        <div className={styles.searchableField}>
                            <label>Customer *</label>
                            <div className={styles.dropdownContainer}>
                                <input
                                    type="text"
                                    value={customerSearch}
                                    onChange={(e) => {
                                        setCustomerSearch(e.target.value);
                                        setShowCustomerDropdown(true);
                                    }}
                                    onFocus={() => setShowCustomerDropdown(true)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            setShowCustomerDropdown(true);
                                        }
                                    }}
                                    placeholder="Search customer by name or phone..."
                                    className={styles.searchInput}
                                    disabled={editMode}
                                />

                                {showCustomerDropdown && !editMode && filteredCustomers.length > 0 && (
                                    <div className={styles.dropdown}>
                                        {filteredCustomers.map(customer => (
                                            <div
                                                key={customer.id}
                                                className={styles.dropdownItem}
                                                onClick={() => handleCustomerSelect(customer)}
                                            >
                                                <div className={styles.customerInfo}>
                                                    <strong>{customer.name} | </strong>
                                                    <small>{customer.phone}</small>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className={styles.itemsSection}>
                        <h5>Invoice Items</h5>
                        {items.map((item, index) => {
                            const hasLowStock = isLowStock(item.product_id, item.quantity, index);
                            const availableStock = getAvailableStock(item.product_id, index);
                            
                            return (
                                <div key={index} className={styles.itemRow}>
                                    <div className={styles.searchableField}>
                                        <label>Product</label>
                                        <div className={styles.dropdownContainer}>
                                            <input
                                                type="text"
                                                value={productSearches[index]}
                                                onChange={(e) => {
                                                    const newSearches = [...productSearches];
                                                    newSearches[index] = e.target.value;
                                                    setProductSearches(newSearches);
                                                    const newDropdowns = [...showProductDropdowns];
                                                    newDropdowns[index] = true;
                                                    setShowProductDropdowns(newDropdowns);
                                                }}
                                                onFocus={() => {
                                                    const newDropdowns = [...showProductDropdowns];
                                                    newDropdowns[index] = true;
                                                    setShowProductDropdowns(newDropdowns);
                                                }}
                                                placeholder="Search product..."
                                                className={styles.searchInputs}
                                            />
                                            {showProductDropdowns[index] && getFilteredProducts(index).length > 0 && (
                                                <div className={styles.dropdown}>
                                                    {getFilteredProducts(index).map(product => {
                                                        const outOfStock = isOutOfStock(Number(product.id), index);
                                                        const displayStock = getAvailableStock(Number(product.id), index);
                                                        return (
                                                            <div
                                                                key={product.id}
                                                                className={styles.dropdownItem}
                                                                onClick={() => !outOfStock && handleProductSelect(index, product)}
                                                                style={{
                                                                    cursor: outOfStock ? 'not-allowed' : 'pointer',
                                                                    opacity: outOfStock ? 0.5 : 1,
                                                                    backgroundColor: outOfStock ? '#fee2e2' : 'transparent'
                                                                }}
                                                            >
                                                                <div className={styles.productInfo}>
                                                                    <strong>{product.name}</strong>
                                                                    <small>
                                                                        {formatCurrency(product.price)} 
                                                                        {outOfStock ? (
                                                                            <span style={{ color: '#dc2626', marginLeft: '8px', fontWeight: 600 }}>
                                                                                (Out of Stock)
                                                                            </span>
                                                                        ) : (
                                                                            <span style={{ color: displayStock < 10 ? '#f59e0b' : '#10b981', marginLeft: '8px' }}>
                                                                                (Stock: {displayStock})
                                                                            </span>
                                                                        )}
                                                                    </small>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <InputField
                                        label="Quantity"
                                        name='quantity'
                                        type="number"
                                        value={item.quantity}
                                        onChange={e => handleItemChange(index, 'quantity', parseInt(e.target.value) || 0)}
                                        style={{
                                            borderColor: hasLowStock ? '#dc2626' : undefined,
                                            backgroundColor: hasLowStock ? '#fee2e2' : undefined
                                        }}
                                    />

                                    <InputField
                                        label={`Empty Return`}
                                        name='empty_stock_count'
                                        type="number"
                                        value={item.empty_stock_count}
                                        onChange={e => handleItemChange(index, 'empty_stock_count', parseInt(e.target.value) || 0)}
                                        placeholder="0"
                                    />

                                    <InputField
                                        label="Unit Price"
                                        name='unit_price'
                                        type="number"
                                        value={item.unit_price}
                                        onChange={e => handleItemChange(index, 'unit_price', parseFloat(e.target.value) || 0)}
                                    />
                                    <InputField
                                        label="Amount"
                                        name='amount'
                                        type="number"
                                        value={item.amount}
                                        disabled
                                    />
                                    {items.length > 1 && (
                                        <Button variant="secondary" onClick={() => removeItem(index)} className={styles.removeBtn}>
                                            Remove
                                        </Button>
                                    )}
                                </div>
                            );
                        })}
                        <Button variant="secondary" onClick={addItem}>+ Add Item</Button>
                    </div>

                    <div className={styles.formRow}>
                        <InputField
                            label="Discount"
                            type="number"
                            name='discount_amount'
                            value={form.discount_amount}
                            onChange={e => setForm({ ...form, discount_amount: e.target.value })}
                            placeholder="0.00"
                        />
                    </div>

                    <div className={styles.totals}>
                        <p>Subtotal: {formatCurrency(totals.subtotal)}</p>
                        <p>CGST: {formatCurrency(totals.cgst)}</p>
                        <p>SGST: {formatCurrency(totals.sgst)}</p>
                        <p>Discount: {formatCurrency(totals.discount)}</p>
                        <h4>Total: {formatCurrency(totals.total)}</h4>
                    </div>

                    <InputField
                        label="Notes"
                        type="textarea"
                        name='notes'
                        value={form.notes}
                        onChange={e => setForm({ ...form, notes: e.target.value })}
                        placeholder="Additional notes..."
                    />

                    <div className={styles.modalActions}>
                        <Button variant="secondary" onClick={() => {
                            setShowModal(false);
                            setEditMode(false);
                            setEditingInvoiceId(null);
                            resetForm();
                        }}>
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={editMode ? handleSaveEdit : handleAdd}>
                            {editMode ? 'Update Invoice' : 'Create Invoice'}
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* View Invoice Modal */}
            <Modal
                isOpen={showViewModal}
                onClose={() => {
                    setShowViewModal(false);
                    setSelectedInvoice(null);
                }}
                title="Invoice Details"
                size="lg"
            >
                {selectedInvoice && (
                    <div className={styles.viewInvoice}>
                        <div className={styles.invoiceHeader}>
                            <h3>{selectedInvoice.invoice_number}</h3>
                        </div>
                        <div className={styles.invoiceInfo}>
                            <p><strong>Customer:</strong> {`${selectedInvoice.customer_name} - ${selectedInvoice.customer_phone}`}</p>
                            <p><strong>Date:</strong> {new Date(selectedInvoice.created_at).toLocaleDateString()}</p>
                            <p><strong>Sub Total:</strong> {getSubTotal(Number(selectedInvoice.total_amount), Number(selectedInvoice.discount_amount), Number(selectedInvoice.cgst_amount), Number(selectedInvoice.sgst_amount))}</p>
                            <p><strong>CGST:</strong> {formatCurrency(selectedInvoice.cgst_amount)}</p>
                            <p><strong>SGST:</strong> {formatCurrency(selectedInvoice.sgst_amount)}</p>
                            <p><strong>Discount:</strong> {formatCurrency(selectedInvoice.discount_amount)}</p>
                            <p><strong>Total:</strong> {formatCurrency(selectedInvoice.total_amount)}</p>
                        </div>
                        {selectedInvoice.items && selectedInvoice.items.length > 0 && (
                            <div className={styles.invoiceItems}>
                                <h4>Items</h4>
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Description</th>
                                            <th>Qty</th>
                                            <th>Empty Return</th>
                                            <th>Price</th>
                                            <th>Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedInvoice.items.map(item => (
                                            <tr key={item.id}>
                                                <td>{item.description}</td>
                                                <td>{item.quantity}</td>
                                                <td>{item.empty_stock_count || 0}</td>
                                                <td>{formatCurrency(item.unit_price)}</td>
                                                <td>{formatCurrency(item.amount)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        {selectedInvoice.notes && (
                            <div className={styles.invoiceNotes}>
                                <h4>Notes</h4>
                                <p>{selectedInvoice.notes}</p>
                            </div>
                        )}
                    </div>
                )}
            </Modal>

            <ToastContainer position="top-right" autoClose={3000} />
        </div>
    );
};

export default InvoiceList;